<template>
  <Card>
    <template #title="">
      <div class="p-d-flex p-justify-between">
        ตั้งค่าแบบประเมิน และการแจ้งเตือน
        <div>
          <Button
            label="บันทึก"
            icon="pi pi-save"
            @click="onSaveDocument"
          ></Button>
          <BackButton class="p-ml-2"></BackButton>
        </div>
      </div>
    </template>
    <template #subtitle="">
      ระบุชุดแบบประเมิน, วันที่เริ่มทำแบบประเมินได้
      และจำนวนวันที่ส่งแจ้งเตือนให้ทำแบบประเมิน
    </template>

    <template #content=""
      ><div class="p-grid">
        <div class="p-col-12">
          <DataTable :value="configs">
            <Column
              field="trainingType"
              header="ประเภทฝึกอบรม"
              headerStyle="width:8rem"
              bodyStyle="width:8rem"
            >
              <template #body="props">
                {{ trainingTypeDesc(props.data.trainingType) }}
              </template>
            </Column>

            <Column field="instructorQuestionId" header="ผู้ฝึกสอน">
              <template #body="props">
                <div class="p-fluid">
                  <InputQuestion
                    v-if="isTrainingType(props.data.trainingType)"
                    v-model="props.data.instructorQuestionId"
                    :showClear="true"
                  ></InputQuestion>
                </div>
              </template>
            </Column>

            <Column field="satisfactionQuestionId" header="ความพึงพอใจ">
              <template #body="props">
                <div class="p-fluid">
                  <InputQuestion
                    v-if="isTrainingType(props.data.trainingType)"
                    v-model="props.data.satisfactionQuestionId"
                    :showClear="true"
                  ></InputQuestion>
                </div>
              </template>
            </Column>

            <Column field="knowledgeQuestionId" header="ความรู้">
              <template #body="props">
                <div class="p-fluid">
                  <InputQuestion
                    v-if="
                      isTrainingType(props.data.trainingType) ||
                        isPositionType(props.data.trainingType)
                    "
                    v-model="props.data.knowledgeQuestionId"
                    :showClear="true"
                  ></InputQuestion>
                </div>
              </template>
            </Column>

            <Column
              field="instructorWaitingPeriod"
              header="ระยะรอ(ผู้ฝึกสอน)"
              headerStyle="width:6rem"
              bodyStyle="width:6rem"
            >
              <template #body="props">
                <div class="p-fluid">
                  <InputNumber
                    v-if="isTrainingType(props.data.trainingType)"
                    class="p-fluid"
                    v-model="props.data.instructorWaitingPeriod"
                  ></InputNumber>
                </div>
              </template>
            </Column>
            <Column
              field="satisfactionWaitingPeriod"
              header="ระยะรอ(ความพอใจ)"
              headerStyle="width:6rem"
              bodyStyle="width:6rem"
            >
              <template #body="props">
                <div class="p-fluid">
                  <InputNumber
                    v-if="isTrainingType(props.data.trainingType)"
                    class="p-fluid"
                    v-model="props.data.satisfactionWaitingPeriod"
                  ></InputNumber>
                </div>
              </template>
            </Column>
            <Column
              field="knowledgeWaitingPeriod"
              header="ระยะรอ(ความรู้)"
              headerStyle="width:6rem"
              bodyStyle="width:6rem"
            >
              <template #body="props">
                <div class="p-fluid">
                  <InputNumber
                    v-if="
                      isTrainingType(props.data.trainingType) ||
                        isPositionType(props.data.trainingType)
                    "
                    class="p-fluid"
                    v-model="props.data.knowledgeWaitingPeriod"
                  ></InputNumber>
                </div>
              </template>
            </Column>
            <Column
              field="dunning1"
              header="ติดตาม(1)"
              headerStyle="width:6rem"
              bodyStyle="width:6rem"
            >
              <template #body="props">
                <div class="p-fluid">
                  <InputNumber v-model="props.data.dunning1"></InputNumber>
                </div>
              </template>
            </Column>
            <Column
              field="dunning2"
              header="ติดตาม(2)"
              headerStyle="width:6rem"
              bodyStyle="width:6rem"
            >
              <template #body="props">
                <div class="p-fluid">
                  <InputNumber
                    class="p-fluid"
                    v-model="props.data.dunning2"
                  ></InputNumber>
                </div>
              </template>
            </Column>
          </DataTable>
        </div></div
    ></template>
  </Card>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { usePlugin } from "@/plugins";
import {
  ConfigQuestionSet,
  GetConfigQuestionSetResponse,
  UpsertConfigQuestionSetRequest,
  UpsertConfigUserRequest,
  TrainingTypeEnum,
} from "@/types";
import InputQuestion from "@/components/InputQuestion.vue";

export default defineComponent({
  name: "ConfigQuestionSetScreen",
  components: { InputQuestion },
  setup() {
    const { setLoading, error, success, axios, trainingTypeDesc } = usePlugin();
    const configs = ref<ConfigQuestionSet[]>([]);

    const loadDocument = async () => {
      setLoading(true);
      try {
        const result = await axios.post<GetConfigQuestionSetResponse>(
          "api/v1/services/configQuestionSet",
          {}
        );

        configs.value = result.data.configQuestionSets ?? [];
        success("ดึงข้อมูลสำเร็จแล้ว");
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onSaveDocument = async () => {
      setLoading(true);
      try {
        await axios.post("api/v1/services/configQuestionSet/upsert", {
          questionSets: configs.value,
        } as UpsertConfigQuestionSetRequest);
        success("บันทึกข้อมูลสำเร็จแล้ว");
        loadDocument();
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const isTrainingType = (input: TrainingTypeEnum) => {
      return [
        TrainingTypeEnum.Internal,
        TrainingTypeEnum.External,
        TrainingTypeEnum.SiteVisit,
      ].includes(input);
    };

    const isPositionType = (input: TrainingTypeEnum) => {
      return [
        TrainingTypeEnum.NewEmployee,
        TrainingTypeEnum.Transfer,
        TrainingTypeEnum.Promote,
      ].includes(input);
    };

    onMounted(() => loadDocument());

    return {
      configs,
      trainingTypeDesc,
      onSaveDocument,
      isTrainingType,
      isPositionType,
    };
  },
});
</script>

<style></style>
